import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Boxes, Package, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { fmtDateTime } from "../lib/format";
import { FormField } from "../components/ui/form-field";
import { useI18n } from "../i18n/I18nContext";

const empty = {
  name: "",
  total_quantity: 1,
  status: "ativo",
  notes: "",
  purchase_value: 0,
  estimated_usage_time: "",
  purchase_place: "",
};

export default function Equipments() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const confirm = useConfirm();

  async function load() {
    const { data } = await api.get("/equipments");
    setItems(data);
  }
  useEffect(() => {
    load();
  }, []);

  async function remove(e) {
    if (!(await confirm({
      title: `${t("Excluir")} "${e.name}"?`,
      description: t("Esta ação não pode ser desfeita."),
      destructive: true,
      confirmText: t("Excluir"),
    }))) return;
    try {
      await api.delete(`/equipments/${e.id}`);
      toast.success(t("Equipamento excluído"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Estoque")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Boxes size={26} /> {t("Equipamentos")}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {t("Cadastre os equipamentos e a quantidade total disponível")}
          </div>
        </div>
        <Button
          data-testid="equipment-new-btn"
          onClick={() => {
            setEditing({ ...empty });
            setOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-1" /> {t("Novo equipamento")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((e) => {
          const pct = e.total_quantity
            ? Math.round((e.used / e.total_quantity) * 100)
            : 0;
          return (
            <div
              className="cm-card p-5"
              key={e.id}
              data-testid={`equipment-card-${e.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Package size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {e.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {e.notes || "—"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setHistoryOf(e)}
                    data-testid={`equipment-history-${e.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    title={t("Histórico de movimentação")}
                  >
                    <HistoryIcon size={18} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(e);
                      setOpen(true);
                    }}
                    data-testid={`equipment-edit-${e.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    title={t("Editar")}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => remove(e)}
                    data-testid={`equipment-delete-${e.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                    title={t("Excluir")}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <Stat label={t("Total")} value={e.total_quantity} />
                <Stat label={t("Em uso")} value={e.used} accent="text-amber-700" />
                <Stat
                  label={t("Disponível")}
                  value={e.available}
                  accent="text-emerald-700"
                />
              </div>
              <div className="mt-3">
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {pct}% {t("distribuído")}
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="cm-card p-10 text-center text-slate-500 col-span-full">
            {t("Nenhum equipamento cadastrado.")}
          </div>
        )}
      </div>

      <EquipmentDialog
        open={open}
        onOpenChange={setOpen}
        item={editing}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
      <MovementsDialog
        equipment={historyOf}
        onClose={() => setHistoryOf(null)}
      />
    </AdminLayout>
  );
}

function MovementsDialog({ equipment, onClose }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState({ team_id: "", from: "", to: "" });

  useEffect(() => {
    if (!equipment) return;
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, [equipment]);

  useEffect(() => {
    if (!equipment) return;
    const qs = new URLSearchParams();
    if (filter.team_id) qs.append("team_id", filter.team_id);
    if (filter.from) qs.append("from", filter.from);
    if (filter.to) qs.append("to", filter.to);
    api
      .get(`/equipments/${equipment.id}/movements?${qs.toString()}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [equipment, filter]);

  return (
    <Dialog open={!!equipment} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="movements-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon size={18} /> {t("Histórico")} — {equipment?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 bg-slate-50 rounded-lg mb-2">
          <select
            data-testid="movements-filter-team"
            className="cm-input text-sm"
            value={filter.team_id}
            onChange={(e) =>
              setFilter((f) => ({ ...f, team_id: e.target.value }))
            }
          >
            <option value="">{t("Todas as equipes")}</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <DatePickerUS
            value={filter.from}
            onChange={(v) => setFilter((f) => ({ ...f, from: v }))}
          />
          <DatePickerUS
            value={filter.to}
            onChange={(v) => setFilter((f) => ({ ...f, to: v }))}
          />
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            {t("Nenhuma movimentação encontrada com esses filtros.")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((m) => (
              <div
                key={m.id}
                className="py-3 flex items-start gap-3"
                data-testid={`movement-${m.id}`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                    m.action === "alocado"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : m.action === "devolvido"
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-slate-100 text-slate-700 border border-slate-200"
                  }`}
                >
                  {m.action === "alocado" ? "+" : m.action === "devolvido" ? "−" : "≈"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {m.action === "alocado"
                      ? t("{qty} alocado à equipe {team}", { qty: m.quantity, team: m.team_name })
                      : m.action === "devolvido"
                        ? t("{qty} devolvido da equipe {team}", { qty: m.quantity, team: m.team_name })
                        : t("Ajuste em {team}", { team: m.team_name })}
                  </div>
                  <div className="text-xs text-slate-500">
                    {m.prev_quantity} → {m.next_quantity} · {t("por")} {m.user_name} ·{" "}
                    {fmtDateTime(m.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Fechar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent = "" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`text-xl font-bold ${accent || "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function EquipmentDialog({ open, onOpenChange, item, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(item || empty);
  useEffect(() => {
    setForm(item || empty);
  }, [item, open]);
  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function save() {
    try {
      const body = {
        ...form,
        total_quantity: parseInt(form.total_quantity) || 0,
        purchase_value: parseFloat(form.purchase_value) || 0,
      };
      if (item?.id) await api.patch(`/equipments/${item.id}`, body);
      else await api.post("/equipments", body);
      toast.success(t("Equipamento salvo"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="equipment-dialog">
        <DialogHeader>
          <DialogTitle>
            {item?.id ? t("Editar equipamento") : t("Novo equipamento")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label={t("Nome do Equipamento")}>
            
            <input
              data-testid="equipment-field-name"
              className="cm-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={t("Ex: Aspirador de pó, Vassoura…")}
            />
</FormField>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField label={t("Quantidade Total")}>
              
              <input
                data-testid="equipment-field-quantity"
                type="number"
                min="0"
                className="cm-input"
                value={form.total_quantity}
                onChange={(e) => set("total_quantity", e.target.value)}
              />
</FormField>
            <FormField label={t("Valor da Compra (US$)")}>
              
              <input
                data-testid="equipment-field-value"
                type="number"
                step="0.01"
                min="0"
                className="cm-input"
                value={form.purchase_value}
                onChange={(e) => set("purchase_value", e.target.value)}
              />
</FormField>
            <FormField label={t("Tempo Estimado de Uso")}>
              
              <input
                data-testid="equipment-field-time"
                className="cm-input"
                placeholder={t("Ex: 2 anos, 18 meses…")}
                value={form.estimated_usage_time || ""}
                onChange={(e) =>
                  set("estimated_usage_time", e.target.value)
                }
              />
</FormField>
            <FormField label={t("Onde Foi Comprado")}>
              
              <input
                data-testid="equipment-field-place"
                className="cm-input"
                placeholder={t("Ex: Home Depot, Amazon…")}
                value={form.purchase_place || ""}
                onChange={(e) => set("purchase_place", e.target.value)}
              />
</FormField>
          </div>
          <FormField label={t("Observações")}>
            
            <textarea
              rows={2}
              className="cm-input"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
            />
</FormField>
          <FormField label={t("Status")}>
            
            <select
              className="cm-input"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value="ativo">{t("Ativo")}</option>
              <option value="manutencao">{t("Em manutenção")}</option>
              <option value="inativo">{t("Inativo")}</option>
            </select>
</FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            data-testid="equipment-save-btn"
            onClick={save}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t("Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
